<template>
  <div class="support-center-page px-4 pb-10">
    <div class="flex flex-col gap-6">
      <shipblu-card class="support-hero-card overflow-hidden">
        <div class="support-hero-content">
          <div class="flex flex-col gap-2 text-left">
            <p class="support-hero-eyebrow">{{ $t('Welcome to the ShipBlu Help Center') }}</p>
            <h1 class="support-hero-title">{{ $t('How can we help you today?') }}</h1>
            <p class="support-hero-subtitle">
              {{ $t('Create a ticket, review your recent requests, or browse quick answers to the most common questions.') }}
            </p>
          </div>
          <div class="support-hero-summary">
            <div class="support-hero-chip">
              <span class="material-symbols-outlined">support_agent</span>
              <div>
                <p class="label">{{ $t('Support Manager') }}</p>
                <p class="value">{{ supportManager ? supportManager.name : $t('Not assigned yet') }}</p>
              </div>
            </div>
            <div class="support-hero-chip" v-if="supportManager && (supportManager.email || supportManager.phone)">
              <span class="material-symbols-outlined">call</span>
              <div>
                <p class="label" v-if="supportManager.phone">{{ formatPhone(supportManager.phone) }}</p>
                <p class="label" v-if="supportManager.email">{{ supportManager.email }}</p>
                <p class="value">{{ $t('Reach out any time') }}</p>
              </div>
            </div>
          </div>
        </div>
      </shipblu-card>

      <div class="grid gap-6 xl:grid-cols-[1.65fr_1fr]">
        <div class="flex flex-col gap-6">
          <shipblu-card class="p-0">
            <div class="ticket-form-card">
              <div class="ticket-form-content">
                <div class="ticket-form-header">
                  <div>
                    <h2>{{ $t('Create New Ticket') }}</h2>
                    <p>{{ $t('Fill out the information below, then click submit button') }}</p>
                  </div>
                  <div class="flex flex-col md:flex-row md:items-center gap-4 text-sm text-slate-500">
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-lg text-primary">today</span>
                      <span>{{ formatDate(new Date()) }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-lg text-primary">badge</span>
                      <span>{{ merchantSummary.merchantCode || '-' }}</span>
                    </div>
                  </div>
                </div>

                <form class="ticket-form" @submit.prevent="submitTicket">
                  <div class="grid gap-4 md:grid-cols-2">
                    <div>
                      <label class="shipblu-label">{{ $t('Select Request Type') }}<span class="required">*</span></label>
                      <v-select
                        class="shipblu-select"
                        :options="requestTypes"
                        label="name"
                        :get-option-key="option => option.id"
                        :reduce="option => option"
                        :placeholder="$t('Choose request type')"
                        v-model="newTicket.requestType"
                        v-validate="'required'"
                        name="request type"
                        @input="filterCategories"
                        :disabled="isSubmitting || isLoadingMetadata"
                      />
                      <span class="input-error" v-if="errors.has('request type')">{{ $t('Please select a request type') }}</span>
                    </div>

                    <div>
                      <label class="shipblu-label">{{ $t('Request Category') }}<span class="required">*</span></label>
                      <v-select
                        class="shipblu-select"
                        :options="filteredCategories"
                        label="name"
                        :get-option-key="option => option.id"
                        :reduce="option => option"
                        :placeholder="$t('Choose request category')"
                        v-model="newTicket.category"
                        v-validate="'required'"
                        name="request category"
                        :disabled="!newTicket.requestType || isSubmitting || isLoadingMetadata"
                      />
                      <span class="input-error" v-if="errors.has('request category')">{{ $t('Please select a request category') }}</span>
                    </div>

                    <div>
                      <label class="shipblu-label">{{ $t('Choose Response Mode') }}<span class="required">*</span></label>
                      <v-select
                        class="shipblu-select"
                        :options="responseModes"
                        label="name"
                        :get-option-key="option => option.value || option.id"
                        :reduce="option => option"
                        :placeholder="$t('Select response mode')"
                        v-model="newTicket.responseMode"
                        v-validate="'required'"
                        name="response mode"
                        :disabled="isSubmitting || isLoadingMetadata"
                      />
                      <span class="input-error" v-if="errors.has('response mode')">{{ $t('Please choose how you would like us to respond') }}</span>
                    </div>

                    <div>
                      <label class="shipblu-label">{{ $t('Ticket Subject') }}<span class="required">*</span></label>
                      <vs-input
                        class="shipblu-input"
                        name="ticket subject"
                        :disabled="isSubmitting"
                        v-validate="'required|min:5'"
                        v-model.trim="newTicket.subject"
                        :class="newTicket.subject ? 'input-customer-info-filled' : ''"
                      />
                      <span class="input-error" v-if="errors.has('ticket subject')">{{ $t('Please add a short subject for your ticket') }}</span>
                    </div>

                    <div>
                      <label class="shipblu-label">{{ $t('Contact Person') }}<span class="required">*</span></label>
                      <vs-input
                        class="shipblu-input"
                        name="contact person"
                        :disabled="isSubmitting"
                        v-validate="'required'"
                        v-model.trim="newTicket.contactName"
                        :class="newTicket.contactName ? 'input-customer-info-filled' : ''"
                      />
                      <span class="input-error" v-if="errors.has('contact person')">{{ $t('Please enter the contact person name') }}</span>
                    </div>

                    <div>
                      <label class="shipblu-label">{{ $t('Phone Number') }}<span class="required">*</span></label>
                      <vs-input
                        class="shipblu-input"
                        name="phone number"
                        :disabled="isSubmitting"
                        v-validate="{ required: true, regex: /(^01[0,1,2,5]\d{8}$|^\u0660\u0661[\u0660,\u0661,\u0662,\u0665][\u0660-\u0669]{8}$)/ }"
                        v-model.trim="newTicket.contactPhone"
                        :class="newTicket.contactPhone ? 'input-customer-info-filled' : ''"
                      />
                      <span class="input-error" v-if="errors.has('phone number')">{{ $t('Enter a valid Egyptian mobile number') }}</span>
                    </div>
                  </div>

                  <div>
                    <label class="shipblu-label">{{ $t('Ticket Description') }}<span class="required">*</span></label>
                    <vs-textarea
                      name="ticket description"
                      rows="5"
                      width="100%"
                      class="shipblu-textarea"
                      :disabled="isSubmitting"
                      v-validate="'required|min:10'"
                      v-model.trim="newTicket.description"
                    />
                    <span class="input-error" v-if="errors.has('ticket description')">{{ $t('Please provide a detailed description') }}</span>
                  </div>

                  <div>
                    <label class="shipblu-label">{{ $t('Attachments') }}</label>
                    <div class="attachments-uploader" @click="triggerAttachmentUpload" role="button" tabindex="0" @keyup.enter="triggerAttachmentUpload" @keyup.space.prevent="triggerAttachmentUpload">
                      <div class="flex items-center gap-3">
                        <span class="material-symbols-outlined">cloud_upload</span>
                        <div>
                          <p>{{ $t('Drop files here or click to upload') }}</p>
                          <span>{{ $t('Max file size: 10 MB each') }}</span>
                        </div>
                      </div>
                      <vs-button size="small" type="border" :disabled="isSubmitting">{{ $t('Browse files') }}</vs-button>
                    </div>
                    <input ref="attachmentsInput" class="hidden" type="file" multiple @change="handleAttachmentChange" />

                    <ul class="attachment-list" v-if="attachments.length">
                      <li v-for="(file, index) in attachments" :key="file.uid" class="attachment-item">
                        <div class="flex items-center gap-3">
                          <span class="material-symbols-outlined text-primary">attach_file</span>
                          <div>
                            <p>{{ file.name }}</p>
                            <span>{{ formatFileSize(file.size) }}</span>
                          </div>
                        </div>
                        <button type="button" @click="removeAttachment(index)" :disabled="isSubmitting">
                          <span class="material-symbols-outlined">close</span>
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4">
                    <div class="ticket-summary-box">
                      <p>{{ $t('Primary Contact') }}</p>
                      <h3>{{ merchantSummary.contactName || newTicket.contactName || '-' }}</h3>
                      <span>{{ merchantSummary.contactEmail || (supportManager && supportManager.email) || '-' }}</span>
                    </div>
                    <vs-button :disabled="isSubmitting || isLoadingMetadata" :loading="isSubmitting" type="filled" class="submit-button">
                      {{ $t('Submit Ticket') }}
                    </vs-button>
                  </div>
                </form>
              </div>

              <div class="ticket-form-sidebar">
                <div class="sidebar-card">
                  <p class="label">{{ $t('Ticket Date') }}</p>
                  <p class="value">{{ formatDate(new Date()) }}</p>
                </div>
                <div class="sidebar-card" v-if="supportManager">
                  <p class="label">{{ $t('Support Contact') }}</p>
                  <p class="value">{{ supportManager.name }}</p>
                  <p class="muted" v-if="supportManager.phone">{{ formatPhone(supportManager.phone) }}</p>
                  <p class="muted" v-if="supportManager.email">{{ supportManager.email }}</p>
                </div>
                <div class="sidebar-card">
                  <p class="label">{{ $t('Merchant Number') }}</p>
                  <p class="value">{{ merchantSummary.merchantCode || '-' }}</p>
                  <p class="muted" v-if="merchantSummary.companyName">{{ merchantSummary.companyName }}</p>
                </div>
                <div class="sidebar-card">
                  <p class="label">{{ $t('Preferred Response Mode') }}</p>
                  <p class="value">{{ newTicket.responseMode ? (newTicket.responseMode.display || newTicket.responseMode.name || newTicket.responseMode.value) : $t('Not set') }}</p>
                  <p class="muted">{{ $t('You can update this preference when submitting a ticket.') }}</p>
                </div>
              </div>
            </div>
          </shipblu-card>

          <shipblu-card class="p-0">
            <div class="history-card">
              <div class="card-header">
                <div>
                  <h2>{{ $t('Latest Support History') }}</h2>
                  <p>{{ $t('Review the latest updates on your support interactions.') }}</p>
                </div>
                <router-link :to="`/${$route.params.lang}/merchant/support-center`" class="text-primary text-sm" v-if="latestTickets.length">
                  {{ $t('View all') }}
                </router-link>
              </div>

              <div v-if="historyLoading" class="history-loading">
                <span class="loader"></span>
                <p>{{ $t('Fetching your tickets...') }}</p>
              </div>

              <div v-else-if="!latestTickets.length" class="empty-state">
                <span class="material-symbols-outlined">confirmation_number</span>
                <p>{{ $t('You have not created any support tickets yet.') }}</p>
                <span>{{ $t('Submit your first ticket to get started.') }}</span>
              </div>

              <div v-else class="history-table">
                <div class="history-table-header">
                  <span>{{ $t('Ticket ID') }}</span>
                  <span>{{ $t('Subject') }}</span>
                  <span>{{ $t('Request Type') }}</span>
                  <span>{{ $t('Created') }}</span>
                  <span class="text-right">{{ $t('Status') }}</span>
                </div>

                <router-link
                  v-for="ticket in latestTickets"
                  :key="ticket.id"
                  :to="ticketDetailsLink(ticket)"
                  class="history-row"
                >
                  <span class="font-medium text-slate-900">{{ ticket.code || ticket.reference || ticket.id }}</span>
                  <span class="truncate" :title="ticket.subject">{{ ticket.subject || '-' }}</span>
                  <span>{{ (ticket.request_type && ticket.request_type.name) || ticket.request_type || '-' }}</span>
                  <span>{{ formatDate(ticket.created_at || ticket.created || ticket.createdAt) }}</span>
                  <span class="text-right">
                    <span class="status-pill" :class="statusPillClass(ticket.status)">{{ formatStatus(ticket.status) }}</span>
                  </span>
                </router-link>
              </div>
            </div>
          </shipblu-card>
        </div>

        <div class="flex flex-col gap-6">
          <shipblu-card class="p-0">
            <div class="info-card">
              <div class="card-header">
                <div>
                  <h2>{{ $t('Your Support Snapshot') }}</h2>
                  <p>{{ $t('Stay on top of your open and recently resolved tickets.') }}</p>
                </div>
              </div>

              <div class="snapshot-grid">
                <div class="snapshot-item">
                  <p class="label">{{ $t('Open Tickets') }}</p>
                  <p class="value">{{ supportSnapshot.open }}</p>
                  <span class="muted">{{ $t('Awaiting action from our team') }}</span>
                </div>
                <div class="snapshot-item">
                  <p class="label">{{ $t('In Progress') }}</p>
                  <p class="value">{{ supportSnapshot.inProgress }}</p>
                  <span class="muted">{{ $t('Currently being handled') }}</span>
                </div>
                <div class="snapshot-item">
                  <p class="label">{{ $t('Resolved This Month') }}</p>
                  <p class="value">{{ supportSnapshot.resolved }}</p>
                  <span class="muted">{{ $t('Great job closing tickets quickly!') }}</span>
                </div>
              </div>
            </div>
          </shipblu-card>

          <shipblu-card class="p-0">
            <div class="faq-card">
              <div class="card-header">
                <div>
                  <h2>{{ $t('Frequently Asked Questions') }}</h2>
                  <p>{{ $t('Quick answers curated by the support team.') }}</p>
                </div>
              </div>

              <div v-if="faqLoading" class="history-loading">
                <span class="loader"></span>
                <p>{{ $t('Loading FAQs...') }}</p>
              </div>

              <div v-else-if="!faqList.length" class="empty-state">
                <span class="material-symbols-outlined">help</span>
                <p>{{ $t('No FAQs available yet.') }}</p>
                <span>{{ $t('Check back soon for helpful resources.') }}</span>
              </div>

              <ul v-else class="faq-list">
                <li v-for="faq in faqList" :key="faq.id" class="faq-item">
                  <button type="button" @click="toggleFaq(faq)" :aria-expanded="faq.expanded ? 'true' : 'false'">
                    <div>
                      <p>{{ faq.question }}</p>
                      <span>{{ (faq.category && faq.category.name) || faq.category || $t('General') }}</span>
                    </div>
                    <span class="material-symbols-outlined" :class="faq.expanded ? 'rotate-180' : ''">expand_more</span>
                  </button>
                  <transition name="faq">
                    <div v-show="faq.expanded" class="answer">
                      <p v-html="faq.answer"></p>
                      <a v-if="faq.url" :href="faq.url" target="_blank" rel="noopener" class="text-primary hover:underline">{{ $t('Learn more') }}</a>
                    </div>
                  </transition>
                </li>
              </ul>
            </div>
          </shipblu-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ShipbluCard from '@/layouts/components/ShipbluCard.vue'
import { sendRequest } from '@/http/axios/requestHelper.js'
import common from '@/assets/utils/common'
import vSelect from 'vue-select'

let fileUID = 0

export default {
  components: {
    ShipbluCard,
    vSelect
  },
  data () {
    return {
      requestTypes: [],
      categories: [],
      filteredCategories: [],
      responseModes: [],
      supportManager: null,
      supportSnapshot: {
        open: 0,
        inProgress: 0,
        resolved: 0
      },
      latestTickets: [],
      faqList: [],
      attachments: [],
      newTicket: {
        requestType: null,
        category: null,
        responseMode: null,
        subject: '',
        description: '',
        contactName: '',
        contactPhone: ''
      },
      merchantSummary: {
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        companyName: '',
        merchantCode: ''
      },
      historyLoading: false,
      faqLoading: false,
      isSubmitting: false,
      isLoadingMetadata: false
    }
  },
  watch: {
    'newTicket.requestType' () {
      this.filterCategories()
    }
  },
  created () {
    this.loadMetadata()
    this.loadTicketHistory()
    this.loadFaqs()
    this.loadMerchantSummary()
  },
  methods: {
    loadMetadata () {
      this.isLoadingMetadata = true
      sendRequest(true, false, this, 'support-center/options', 'get', null, true,
        (response) => {
          const data = response.data || {}
          this.requestTypes = data.request_types || []
          this.categories = data.categories || []
          this.filteredCategories = this.categories
          this.responseModes = (data.response_modes || []).map(mode => ({
            name: mode.display || mode.name || mode.value || mode,
            value: mode.value || mode.code || mode.id || mode,
            display: mode.display || mode.name || mode.value || mode
          }))
          this.supportManager = data.support_manager || data.account_manager || null
          if (data.snapshot) {
            this.supportSnapshot = {
              open: data.snapshot.open || 0,
              inProgress: data.snapshot.in_progress || data.snapshot.inProgress || 0,
              resolved: data.snapshot.resolved || 0
            }
          }
          if (this.supportManager) {
            this.merchantSummary.contactEmail = this.supportManager.email || this.merchantSummary.contactEmail
          }
          this.isLoadingMetadata = false
        },
        () => {
          this.isLoadingMetadata = false
        }
      )
    },
    loadTicketHistory () {
      this.historyLoading = true
      sendRequest(true, false, this, 'support-center/tickets?limit=6', 'get', null, true,
        (response) => {
          const data = response.data || {}
          this.latestTickets = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : [])
          if (data.snapshot) {
            this.supportSnapshot = {
              open: data.snapshot.open || 0,
              inProgress: data.snapshot.in_progress || data.snapshot.inProgress || 0,
              resolved: data.snapshot.resolved || 0
            }
          }
          this.historyLoading = false
        },
        () => {
          this.historyLoading = false
        }
      )
    },
    loadFaqs () {
      this.faqLoading = true
      sendRequest(true, false, this, 'support-center/faqs', 'get', null, true,
        (response) => {
          const data = response.data || []
          this.faqList = (Array.isArray(data.results) ? data.results : data).map(item => ({
            ...item,
            expanded: false
          }))
          this.faqLoading = false
        },
        () => {
          this.faqLoading = false
        }
      )
    },
    async loadMerchantSummary () {
      try {
        const merchantData = await common.checkMerchantInLocalStorage(this)
        if (merchantData) {
          const userMerchant = merchantData.userMerchantData || {}
          const merchant = merchantData.merchantData || {}
          this.merchantSummary = {
            contactName: `${userMerchant.first_name || ''} ${userMerchant.last_name || ''}`.trim() || merchant.contact_person || '',
            contactPhone: userMerchant.phone || merchant.contact_phone || '',
            contactEmail: userMerchant.email || merchant.email || '',
            companyName: merchant.name || '',
            merchantCode: merchant.merchant_code || merchant.external_id || merchant.id || ''
          }
          if (!this.newTicket.contactName) this.newTicket.contactName = this.merchantSummary.contactName
          if (!this.newTicket.contactPhone) this.newTicket.contactPhone = this.merchantSummary.contactPhone
        }
      } catch (error) {
        // Ignore silently; info is optional
      }
    },
    filterCategories () {
      const selectedType = this.newTicket.requestType
      if (!selectedType) {
        this.filteredCategories = this.categories
        return
      }
      const typeId = selectedType.id || selectedType.value || selectedType.code
      this.filteredCategories = this.categories.filter(category => {
        if (!category) return false
        const categoryType = category.request_type || category.requestType || category.request_type_id || category.requestTypeId
        if (categoryType && typeof categoryType === 'object') {
          return categoryType.id === typeId || categoryType.value === typeId
        }
        return categoryType === typeId
      })
      if (!this.filteredCategories.some(category => category === this.newTicket.category)) {
        this.newTicket.category = null
      }
    },
    formatDate (date) {
      if (!date) return '-'
      const parsed = typeof date === 'string' ? new Date(date) : date
      if (Number.isNaN(parsed.getTime())) return '-'
      return parsed.toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    },
    formatPhone (phone) {
      if (!phone) return ''
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')
    },
    formatStatus (status) {
      if (!status) return this.$t('Pending')
      const normalized = status.toString().replace(/_/g, ' ')
      return normalized.replace(/\b\w/g, (char) => char.toUpperCase())
    },
    statusPillClass (status) {
      const normalized = (status || '').toString().toLowerCase()
      if (['resolved', 'closed', 'completed', 'approved'].includes(normalized)) {
        return 'bg-emerald-100 text-emerald-700'
      }
      if (['in_progress', 'pending', 'open', 'processing'].includes(normalized)) {
        return 'bg-blue-100 text-blue-700'
      }
      if (['cancelled', 'rejected', 'failed'].includes(normalized)) {
        return 'bg-rose-100 text-rose-700'
      }
      return 'bg-slate-100 text-slate-700'
    },
    formatFileSize (size) {
      if (!size && size !== 0) return ''
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    triggerAttachmentUpload () {
      if (this.isSubmitting) return
      this.$refs.attachmentsInput.click()
    },
    handleAttachmentChange (event) {
      const files = Array.from(event.target.files || [])
      const newFiles = []
      files.forEach(file => {
        const sizeInMB = file.size / (1024 * 1024)
        if (sizeInMB > 10) {
          this.$vs.notify({
            title: this.$t('File too large'),
            text: this.$t('Please upload files smaller than 10 MB.'),
            color: 'warning',
            position: 'top-center'
          })
          return
        }
        newFiles.push({
          file,
          name: file.name,
          size: file.size,
          uid: `${Date.now()}-${fileUID++}`
        })
      })
      this.attachments = [...this.attachments, ...newFiles]
      event.target.value = ''
    },
    removeAttachment (index) {
      this.attachments.splice(index, 1)
    },
    buildTicketPayload () {
      const payload = new FormData()
      if (this.newTicket.requestType) {
        payload.append('request_type', this.newTicket.requestType.id || this.newTicket.requestType.value || this.newTicket.requestType.code)
      }
      if (this.newTicket.category) {
        payload.append('category', this.newTicket.category.id || this.newTicket.category.value || this.newTicket.category.code)
      }
      if (this.newTicket.responseMode) {
        payload.append('response_mode', this.newTicket.responseMode.value || this.newTicket.responseMode.id || this.newTicket.responseMode.code)
      }
      payload.append('subject', this.newTicket.subject)
      payload.append('description', this.newTicket.description)
      payload.append('contact_name', this.newTicket.contactName)
      payload.append('contact_phone', this.newTicket.contactPhone)
      this.attachments.forEach((fileWrapper, index) => {
        payload.append(`attachments[${index}]`, fileWrapper.file)
      })
      return payload
    },
    resetForm () {
      this.newTicket = {
        requestType: null,
        category: null,
        responseMode: null,
        subject: '',
        description: '',
        contactName: this.merchantSummary.contactName || '',
        contactPhone: this.merchantSummary.contactPhone || ''
      }
      this.attachments = []
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
    submitTicket () {
      if (this.isSubmitting) return
      this.$validator.validateAll().then(valid => {
        if (!valid) return
        this.isSubmitting = true
        const payload = this.buildTicketPayload()
        sendRequest(false, false, this, 'support-center/tickets', 'post', payload, true,
          (response) => {
            this.isSubmitting = false
            this.$vs.notify({
              title: this.$t('Success'),
              text: this.$t('Your support ticket has been submitted successfully!'),
              color: 'success',
              position: 'top-center'
            })
            this.resetForm()
            this.loadTicketHistory()
            if (response && response.data && response.data.ticket_code) {
              this.$router.push({
                name: 'merchant-support-ticket-details',
                params: {
                  lang: this.$route.params.lang,
                  ticketID: response.data.ticket_code || response.data.id
                }
              }).catch(() => {})
            }
          },
          (error) => {
            this.isSubmitting = false
            common.notifyResponseError(this, error)
          }
        )
      })
    },
    ticketDetailsLink (ticket) {
      const ticketId = ticket.ticket_code || ticket.code || ticket.reference || ticket.id
      return `/${this.$route.params.lang}/merchant/support-center/tickets/${ticketId}`
    },
    toggleFaq (faq) {
      faq.expanded = !faq.expanded
    }
  }
}
</script>

<style lang="scss" scoped>
.support-center-page {
  background-color: #f8fafc;
}

.support-hero-card {
  padding: 32px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(135deg, #ffffff 0%, #eff6ff 80%);
}

.support-hero-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
  }
}

.support-hero-eyebrow {
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.support-hero-title {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.support-hero-subtitle {
  font-size: 16px;
  color: #475569;
  max-width: 620px;
}

.support-hero-summary {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.support-hero-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #1d4ed81a;
  border-radius: 16px;
  color: #1d4ed8;
  min-width: 220px;
  span.material-symbols-outlined {
    font-size: 28px;
  }
  .label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.7;
  }
  .value {
    font-size: 15px;
    font-weight: 600;
    color: #1d4ed8;
  }
}

.ticket-form-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0;

  @media (min-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) 280px;
  }
}

.ticket-form-content {
  padding: 32px;
}

.ticket-form-sidebar {
  border-left: 1px solid rgba(148, 163, 184, 0.25);
  background: #f8fafc;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 1199px) {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.25);
    padding: 24px 24px 32px;
  }
}

.ticket-form-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    color: #64748b;
  }
}

.ticket-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.shipblu-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
  margin-left: 4px;
}

.shipblu-input .vs-input--input,
.shipblu-textarea textarea {
  background: #ffffff;
}

.shipblu-select ::v-deep .vs__dropdown-toggle {
  padding: 10px 12px;
  min-height: 44px;
  border-radius: 10px;
  border-color: #d0d7e2;
}

.shipblu-select ::v-deep .vs__selected {
  font-size: 14px;
  color: #0f172a;
}

.shipblu-select ::v-deep .vs__dropdown-menu {
  border-radius: 12px;
}

.shipblu-textarea ::v-deep textarea {
  border-radius: 12px;
  border-color: #d0d7e2;
}

.attachments-uploader {
  border: 1px dashed #94a3b8;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: #2563eb;
    background: #eff6ff;
  }

  span.material-symbols-outlined {
    font-size: 28px;
    color: #2563eb;
  }

  p {
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
  }

  span {
    display: block;
    font-size: 12px;
    color: #64748b;
  }
}

.attachment-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.attachment-item {
  background: #f1f5f9;
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #0f172a;

  span.material-symbols-outlined {
    font-size: 22px;
  }

  button {
    background: transparent;
    border: none;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0;

    &:hover {
      color: #ef4444;
    }
  }
}

.ticket-summary-box {
  background: linear-gradient(135deg, #1d4ed810 0%, #2563eb20 100%);
  border-radius: 16px;
  padding: 16px 20px;
  min-width: 240px;
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 4px 0;
  }
  span {
    font-size: 13px;
    color: #475569;
  }
}

.submit-button {
  min-width: 180px;
  align-self: flex-end;
}

.sidebar-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
  }

  .value {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
  }

  .muted {
    font-size: 13px;
    color: #475569;
  }
}

.history-card,
.info-card,
.faq-card {
  padding: 28px 32px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    color: #64748b;
  }
}

.history-loading,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
  text-align: center;
  color: #64748b;

  span.material-symbols-outlined {
    font-size: 36px;
    color: #94a3b8;
  }
}

.loader {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #bfdbfe;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.history-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-table-header,
.history-row {
  display: grid;
  grid-template-columns: 1.1fr 2.1fr 1.5fr 1.2fr 1fr;
  gap: 16px;
  align-items: center;
}

.history-table-header {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
}

.history-row {
  padding: 16px 18px;
  background: #f8fafc;
  border-radius: 14px;
  color: #0f172a;
  font-size: 14px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 18px rgba(15, 23, 42, 0.08);
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
}

.snapshot-grid {
  display: grid;
  gap: 16px;
}

.snapshot-item {
  background: #f1f5f9;
  padding: 20px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 13px;
    color: #64748b;
  }

  .value {
    font-size: 26px;
    font-weight: 700;
    color: #1d4ed8;
  }

  .muted {
    font-size: 12px;
    color: #475569;
  }
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;

  button {
    width: 100%;
    padding: 16px 20px;
    background: #fff;
    border: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #0f172a;
    cursor: pointer;
    text-align: left;

    span.material-symbols-outlined {
      transition: transform 0.2s ease;
    }

    div span {
      display: block;
      font-size: 12px;
      color: #64748b;
      margin-top: 6px;
    }
  }

  .answer {
    padding: 0 20px 20px;
    color: #475569;
    font-size: 14px;
    line-height: 1.6;
  }
}

.faq-enter-active,
.faq-leave-active {
  transition: all 0.2s ease;
}

.faq-enter,
.faq-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.input-error {
  margin-top: 6px;
  font-size: 12px;
  color: #ef4444;
}

@media (max-width: 1024px) {
  .history-table-header,
  .history-row {
    grid-template-columns: 1fr 1.6fr 1fr;
  }

  .history-table-header span:nth-child(3),
  .history-table-header span:nth-child(4),
  .history-row span:nth-child(3),
  .history-row span:nth-child(4) {
    display: none;
  }
}
</style>